/**
 * Config des produits proposés à l'étape « Pour quel(s) produit(s)… ».
 *
 * Deux leviers, du plus spécifique au plus général :
 *
 *   1. Attribut `produits` du custom element — liste libre, séparée par des
 *      virgules, posée directement dans l'embed Webflow :
 *        <komilfo-funnel produits="pergola, store, volet"></komilfo-funnel>
 *      C'est la voie recommandée : plus besoin de toucher au code pour une
 *      nouvelle LP.
 *   2. Attribut `variant` — variantes nommées, câblées ici (historique, gardé
 *      pour les LP déjà en ligne comme /solaire).
 *
 * Absence des deux (ou valeurs non reconnues) ⇒ liste complète
 * (DEFAULT_PRODUCTS), donc aucune LP existante n'est impactée.
 *
 * IMPORTANT : chaque libellé doit exister dans public.produits.form_value côté
 * back-office, sinon le produit est silencieusement ignoré à la soumission.
 */

export const DEFAULT_PRODUCTS = [
  "Pergola",
  "Véranda",
  "Store",
  "Fenêtre / Baie Vitrée",
  "Volet",
  "Porte d'entrée",
  "Porte de garage",
  "Carport / Abris de voiture",
  "Portail / Clôture",
  "Maison Connectée",
];

/**
 * Produits hors liste par défaut mais adressables via l'attribut `produits`
 * (ils existent bien dans public.produits.form_value).
 */
export const EXTRA_PRODUCTS = ["Store intérieur", "Store extérieur"];

export const PRODUCT_VARIANTS: Record<string, string[]> = {
  solaire: ["Pergola", "Store intérieur", "Store extérieur", "Volet"],
};

/**
 * Mots vides ignorés lors de la normalisation : ils ne portent aucun sens
 * discriminant et leur présence varie selon comment l'intégrateur écrit
 * ("porte de garage" / "porte garage").
 */
const STOP_WORDS = new Set([
  "de", "du", "des", "d", "la", "le", "les", "l", "et", "a", "au", "aux", "en",
]);

/**
 * Réduit un libellé saisi à une clé comparable : sans accents, sans casse,
 * sans ponctuation, sans pluriels ni mots vides.
 *
 *   "Portes d'entrée" → "porteentree"
 *   "FENÊTRE / BAIE VITRÉE" → "fenetrebaievitree"
 *
 * Le "s" final n'est retiré que sur les mots de plus de 3 lettres, pour ne pas
 * écraser des mots courts ("des" reste "des", filtré ensuite comme mot vide).
 */
export function normalizeProductToken(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((word) => (word.length > 3 && word.endsWith("s") ? word.slice(0, -1) : word))
    .filter((word) => !STOP_WORDS.has(word))
    .join("");
}

/** clé normalisée → libellé canonique envoyé au back-office */
const PRODUCT_LOOKUP: Record<string, string> = {};

function register(label: string, aliases: string[] = []) {
  for (const value of [label, ...aliases]) {
    const key = normalizeProductToken(value);
    if (key) PRODUCT_LOOKUP[key] = label;
  }
}

// Les pluriels et la ponctuation sont déjà absorbés par normalizeProductToken :
// on ne liste ici que les raccourcis réellement différents du libellé complet.
register("Pergola");
register("Véranda");
register("Store");
register("Fenêtre / Baie Vitrée", ["fenetre", "baie", "baie vitree", "fenetre baie"]);
register("Volet");
register("Porte d'entrée", ["porte entree", "entree"]);
register("Porte de garage", ["garage", "porte garage"]);
register("Carport / Abris de voiture", ["carport", "abri", "carport abri", "abri voiture"]);
register("Portail / Clôture", ["portail", "cloture"]);
register("Maison Connectée", ["maison connectee", "domotique"]);
register("Store intérieur", ["store interieur"]);
register("Store extérieur", ["store exterieur"]);

/** Tous les libellés adressables par l'attribut `produits`. */
export const CATALOG_PRODUCTS = [...DEFAULT_PRODUCTS, ...EXTRA_PRODUCTS];

/**
 * Parse l'attribut `produits` : liste séparée par des virgules (ou `;` / `|`).
 * Les libellés inconnus sont ignorés (avec un warn console pour l'intégrateur),
 * les doublons dédupliqués, et l'ordre de saisie est conservé — c'est lui qui
 * décide de l'ordre d'affichage des boutons.
 */
export function parseProductsAttribute(attr?: string | null): string[] {
  if (!attr) return [];

  const products: string[] = [];
  const unknown: string[] = [];

  for (const raw of attr.split(/[,;|\n]/)) {
    const key = normalizeProductToken(raw);
    if (!key) continue;
    const label = PRODUCT_LOOKUP[key];
    if (!label) {
      unknown.push(raw.trim());
      continue;
    }
    if (!products.includes(label)) products.push(label);
  }

  if (unknown.length > 0 && typeof console !== "undefined") {
    console.warn(
      `[komilfo-funnel] produits non reconnus et ignorés : ${unknown.join(", ")}. ` +
        `Valeurs acceptées : ${CATALOG_PRODUCTS.join(", ")}.`,
    );
  }

  return products;
}

export function getProductsForVariant(variant?: string | null): string[] {
  const key = (variant ?? "").trim().toLowerCase();
  return PRODUCT_VARIANTS[key] ?? DEFAULT_PRODUCTS;
}

/**
 * Liste finale affichée : `produits` prime sur `variant`, qui prime sur la
 * liste complète. Un attribut `produits` dont aucun libellé n'est reconnu se
 * comporte comme s'il était absent (on préfère un formulaire complet à un
 * formulaire vide).
 */
export function resolveProducts(options: {
  produits?: string | null;
  variant?: string | null;
}): string[] {
  const explicit = parseProductsAttribute(options.produits);
  if (explicit.length > 0) return explicit;
  return getProductsForVariant(options.variant);
}

/**
 * Nombre de colonnes de la grille produits en desktop.
 *
 * Nombre pair ⇒ 2 colonnes (lignes pleines : 4 produits = 2+2).
 * Nombre impair ⇒ 3 colonnes, comme la liste complète de 9.
 * Produit unique ⇒ pleine largeur, un bouton seul au tiers de la largeur
 * casserait la mise en page.
 */
export function getProductColumns(count: number): 1 | 2 | 3 {
  if (count <= 1) return 1;
  return count % 2 === 0 ? 2 : 3;
}
